import { useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { NativeSelect } from '@/components/ui/Input';
import { TabBar } from '@/components/ui/TabBar';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import { ErrorState, LoadingState } from '@/components/ui/States';
import { toast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/cn';
import NewLabOrderDialog from '@/features/laboratory/NewLabOrderDialog';
import { useDoctors, useEncounterMutation, useErMeta, useWorkspace } from './hooks';
import { markSeen, pageDoctorAgain, patchEncounter } from './api';
import { DoctorCantComeBanner, DoctorPageStatus } from './DoctorAlertPopup';
import {
  ARRIVAL_MODE_LABEL, DoorToDoctor, FlagIcons, IdentityBadge, LevelPill, StatusBadge, errorInfo, minutesLabel, timeLabel,
} from './ui';
import TriagePanel from './TriagePanel';
import IdentityResolveDialog from './IdentityResolveDialog';
import OverviewSection from './sections/OverviewSection';
import NotesSection from './sections/NotesSection';
import MedicationsSection from './sections/MedicationsSection';
import ProceduresConsultsSection from './sections/ProceduresConsultsSection';
import LabsSection from './sections/LabsSection';
import BelongingsConsentSection from './sections/BelongingsConsentSection';
import ChargesSection from './sections/ChargesSection';
import MlcSection from './sections/MlcSection';
import DispositionSection from './sections/DispositionSection';
import EdDocumentAssistant from './documents/EdDocumentAssistant';

type Tab = 'overview' | 'notes' | 'meds' | 'procedures' | 'labs' | 'belongings' | 'charges' | 'mlc' | 'disposition' | 'documents';

/**
 * One encounter's workspace (design §7) — the goal is to grasp the patient in
 * 15–20 seconds: who (identity status), how sick (level, NEWS2), how long
 * (clocks), what is outstanding (open items) — then act. Every section is
 * available at any time between arrival and departure (D1); writes stop at
 * departure except identity, MLC and belongings, which legitimately continue.
 */
export default function EncounterWorkspace({ encounterId, onBack }: { encounterId: number; onBack: () => void }) {
  const { user } = useAuth();
  const ws = useWorkspace(encounterId);
  const meta = useErMeta();
  const doctors = useDoctors();
  const [tab, setTab] = useState<Tab>('overview');
  const [triageOpen, setTriageOpen] = useState(false);
  const [labOpen, setLabOpen] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [unflagOpen, setUnflagOpen] = useState(false);
  const [unflagError, setUnflagError] = useState<string | null>(null);
  const patch = useEncounterMutation(encounterId, (body: Parameters<typeof patchEncounter>[1]) => patchEncounter(encounterId, body));
  const seen = useEncounterMutation(encounterId, () => markSeen(encounterId));
  const repage = useEncounterMutation(encounterId, () => pageDoctorAgain(encounterId));
  const doctorSelect = useRef<HTMLSelectElement>(null);
  const [pickDoctor, setPickDoctor] = useState(false);

  if (ws.isLoading) return <LoadingState label="Opening encounter…" />;
  if (ws.isError) return <ErrorState message={(ws.error as Error).message} onRetry={() => ws.refetch()} />;
  const w = ws.data!;
  const enc = w.encounter;
  const latest = w.triage[0];
  const modules = user?.modules ?? [];
  const has = (m: string) => modules.includes(m);
  const open = enc.status !== 'departed';
  const full = w.settings.mode === 'full';

  const pageAgain = () => repage.mutateAsync(undefined)
    .then(() => toast({ title: 'Doctor paged again', variant: 'success' }))
    .catch((e) => toast({ title: 'Could not page', description: errorInfo(e).message, variant: 'danger' }));
  // "Choose another doctor": bring the attending dropdown into view and open it.
  const chooseAnotherDoctor = () => {
    setPickDoctor(true);
    const el = doctorSelect.current;
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.focus();
    try { (el as HTMLSelectElement & { showPicker?: () => void }).showPicker?.(); } catch { /* not supported — focus is enough */ }
  };
  const mlcNumber = w.mlc && 'mlc_number' in w.mlc ? w.mlc.mlc_number : null;
  const mlcIntimated = !!w.mlc && (w.mlc.restricted ? w.mlc.intimated : !!w.mlc.intimation_sent_at);
  const declinedDoctorId =w.doctor_alert?.status === 'declined' ? w.doctor_alert.doctor_id : null;

  const doPatch = async (body: Parameters<typeof patchEncounter>[1], ok?: string) => {
    try {
      const r = await patch.mutateAsync(body);
      if (r.doctor_alert) toast({ title: `Dr ${r.attending_doctor_name ?? ''} paged`, description: 'They get a popup on their screen. Accepting holds their OPD consultation, if any.', variant: 'success' });
      else if (ok) toast({ title: ok, variant: 'success' });
    } catch (e) { toast({ title: 'Could not update', description: errorInfo(e).message, variant: 'danger' }); }
  };

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'overview', label: 'Overview' },
    { id: 'notes', label: `Notes (${w.notes.filter((n) => !n.superseded_by_note_id).length})` },
    { id: 'meds', label: `Medications (${w.med_orders.length})` },
    { id: 'procedures', label: 'Procedures & consults' },
    { id: 'labs', label: `Labs (${w.labs.length})` },
    { id: 'belongings', label: 'Belongings & consent' },
    ...(has('er:charges') ? [{ id: 'charges' as Tab, label: `Charges (${w.charges.length})` }] : []),
    ...(enc.is_mlc || w.mlc ? [{ id: 'mlc' as Tab, label: '⚖ MLC' }] : []),
    { id: 'disposition', label: enc.disposition ? 'Disposition ✓' : 'Disposition' },
    { id: 'documents', label: '📝 Documents' },
  ];

  const bays = (meta.data?.bays ?? []).filter((b) => !b.occupied_by_encounter_id || b.id === enc.bay_id);

  return (
    <div className="fade-in space-y-3">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>← Board</Button>
        <span className="text-[12px] text-ink-muted">{enc.er_number} · {enc.visit_code}</span>
      </div>

      {/* ── Header ── */}
      <div className={cn('rounded-xl border bg-card p-4', enc.is_mlc ? 'border-warn' : 'border-border')}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[19px] font-bold text-ink">{enc.patient_name}</h2>
              <IdentityBadge status={enc.identity_status} />
              <LevelPill color={latest?.level_color} label={latest?.level_label} />
              <StatusBadge status={enc.status} />
              {enc.is_mlc && <Badge variant="warn">⚖ MLC{w.mlc && 'mlc_number' in w.mlc && w.mlc.mlc_number ? ` ${w.mlc.mlc_number}` : ''}</Badge>}
              {enc.deceased && <Badge variant="danger">Deceased</Badge>}
            </div>
            <div className="mt-1 text-[12px] text-ink-muted">
              {enc.uhid}
              {enc.age != null ? ` · ${enc.age}y` : enc.approx_age_years != null ? ` · ~${enc.approx_age_years}y` : ''}
              {enc.gender ? ` · ${enc.gender}` : ''}
              {enc.arrival_mode ? ` · ${ARRIVAL_MODE_LABEL[enc.arrival_mode]}` : ''}
              {' · arrived '}{timeLabel(enc.arrived_at)}
              {enc.chief_complaint ? ` · ${enc.chief_complaint}` : ''}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[12px]">
              <span className="font-semibold">Allergies:</span>
              {w.allergies.length === 0
                ? <span className="text-ink-muted">none recorded</span>
                : w.allergies.map((a) => <Badge key={a.id} variant="danger">{a.substance}{a.reaction ? ` (${a.reaction})` : ''}</Badge>)}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[12px] sm:grid-cols-4">
            <div><div className="text-ink-muted">In ED</div><div className="font-mono text-[15px] font-semibold">{minutesLabel(w.clocks.minutes_since_arrival)}</div></div>
            <div><div className="text-ink-muted">Door → doctor</div><DoorToDoctor clocks={w.clocks} seen={!!enc.first_doctor_at} /></div>
            {latest?.early_warning?.news2 && (
              <div><div className="text-ink-muted">NEWS2</div><div className="font-mono text-[15px] font-semibold">{latest.early_warning.news2.score} <span className="text-[11px] font-normal">{latest.early_warning.news2.risk}</span></div></div>
            )}
            {w.clocks.retriage && <div><div className="text-ink-muted">Re-triage</div><div className={cn('font-semibold', w.clocks.retriage.overdue && 'text-danger-ink')}>{w.clocks.retriage.overdue ? `${minutesLabel(w.clocks.retriage.overdue_min)} overdue` : `due ${timeLabel(w.clocks.retriage.due_at)}`}</div></div>}
            {w.clocks.observation && <div><div className="text-ink-muted">Observation</div><div className={cn('font-semibold', w.clocks.observation.breached && 'text-danger-ink')}>{minutesLabel(w.clocks.observation.minutes_left)} left</div></div>}
            {w.clocks.boarding && <div><div className="text-ink-muted">Boarding</div><div className={cn('font-semibold', w.clocks.boarding.alert && 'text-danger-ink')}>{minutesLabel(w.clocks.boarding.minutes)}</div></div>}
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <FlagIcons flags={w.clocks.flags} />
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {has('er:clinical') && open && (
              <NativeSelect ref={doctorSelect} className={cn('h-8 w-[180px] text-[12px]', pickDoctor && 'ring-2 ring-danger')}
                value={enc.attending_doctor_id ?? ''} title="Attending doctor" onBlur={() => setPickDoctor(false)}
                onChange={(e) => { setPickDoctor(false); doPatch({ attending_doctor_id: e.target.value ? Number(e.target.value) : null }, 'Attending updated'); }}>
                <option value="">Attending: —</option>
                {(doctors.data ?? []).map((d) => (
                  <option key={d.id} value={d.id}>Dr: {d.full_name}{d.id === declinedDoctorId ? " — can't come" : ''}</option>
                ))}
              </NativeSelect>
            )}
            {w.doctor_alert && (
              <DoctorPageStatus status={w.doctor_alert.status} pagedAt={w.doctor_alert.paged_at}
                declineReason={w.doctor_alert.decline_reason} />
            )}
            {has('er:clinical') && open && !!enc.attending_doctor_id && enc.attending_doctor_id !== user?.id
              && (!w.doctor_alert || w.doctor_alert.status === 'pending') && (
              <Button size="sm" variant="ghost" loading={repage.isPending} title="Send the popup to the attending doctor again" onClick={pageAgain}>
                📟 Page again
              </Button>
            )}
            {has('er:clinical') && open && full && (
              <NativeSelect className="h-8 w-[130px] text-[12px]" value={enc.bay_id ?? ''} title="Bay"
                onChange={(e) => doPatch({ bay_id: e.target.value ? Number(e.target.value) : null }, 'Bay updated')}>
                <option value="">Bay: —</option>
                {bays.map((b) => <option key={b.id} value={b.id}>{b.code} ({b.zone})</option>)}
              </NativeSelect>
            )}
            {has('er:clinical') && open && (
              <NativeSelect className="h-8 w-[170px] text-[12px]" value={enc.payer_category} title="Payer"
                onChange={(e) => doPatch({ payer_category: e.target.value }, 'Payer updated')}>
                {(meta.data?.payer_schemes ?? []).map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
              </NativeSelect>
            )}
          </div>
        </div>

        {open && w.doctor_alert && (
          <DoctorCantComeBanner alert={w.doctor_alert} doctorName={enc.attending_doctor_name ?? null}
            onChooseAnother={chooseAnotherDoctor} onPageAgain={pageAgain} paging={repage.isPending}
            canAct={has('er:clinical')} />
        )}

        <div className="mt-3 flex flex-wrap gap-2">
          {has('er:triage') && open && enc.status !== 'disposition_decided' && (
            <Button size="sm" variant={enc.triaged_at ? 'ghost' : 'primary'} onClick={() => setTriageOpen(true)}>{enc.triaged_at ? '🔁 Re-triage' : '🩺 Triage'}</Button>
          )}
          {user?.role === 'Doctor' && open && !enc.first_doctor_at && (
            <Button size="sm" loading={seen.isPending} onClick={() => seen.mutateAsync(undefined).catch((e) => toast({ title: errorInfo(e).message, variant: 'danger' }))}>👋 I'm seeing this patient</Button>
          )}
          {open && has('lab:orders') && <Button size="sm" variant="ghost" onClick={() => setLabOpen(true)}>🧪 Lab order ({w.settings.default_lab_priority.toUpperCase()})</Button>}
          {has('er:clinical') && open && !enc.is_mlc && (
            <Button size="sm" variant="ghost" onClick={() => doPatch({ is_mlc: true }, 'Flagged as MLC')}>⚖ Flag MLC</Button>
          )}
          {has('er:mlc') && open && enc.is_mlc && (
            <Button size="sm" variant="ghost" disabled={mlcIntimated}
              title={mlcIntimated
                ? 'The police have already been informed — correct it with the police station and record that in an ED note'
                : 'Chosen by mistake? Remove the MLC flag with a reason (kept in the audit trail)'}
              onClick={() => { setUnflagError(null); setUnflagOpen(true); }}>↩ Remove MLC flag</Button>
          )}
          {has('er:register') && enc.identity_status !== 'verified' && (
            <Button size="sm" variant="ghost" onClick={() => setIdentityOpen(true)}>❓ Resolve identity</Button>
          )}
          {has('er:clinical') && (
            <Button size="sm" variant={tab === 'documents' ? 'primary' : 'ghost'} title="Discharge summary, referral letter, consultation note — drafted with the AI assistant"
              onClick={() => setTab('documents')}>📝 Documents</Button>
          )}
          {has('er:disposition') && open && (
            <Button size="sm" variant="success" onClick={() => setTab('disposition')}>{enc.status === 'disposition_decided' ? '🛏 Departure' : '➜ Disposition'}</Button>
          )}
        </div>
      </div>

      {w.open_items.some((i) => i.severity === 'red') && (
        <div className="rounded-xl border border-danger bg-danger-soft px-4 py-2 text-[12px] text-danger-ink">
          {w.open_items.filter((i) => i.severity === 'red').map((i, k) => <div key={k}>⚠ {i.message}</div>)}
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} size="sm" />

      {tab === 'overview' && <OverviewSection ws={w} />}
      {tab === 'notes' && <NotesSection ws={w} />}
      {tab === 'meds' && <MedicationsSection ws={w} />}
      {tab === 'procedures' && <ProceduresConsultsSection ws={w} />}
      {tab === 'labs' && <LabsSection ws={w} onOrder={() => setLabOpen(true)} />}
      {tab === 'belongings' && <BelongingsConsentSection ws={w} />}
      {tab === 'charges' && <ChargesSection ws={w} />}
      {tab === 'mlc' && <MlcSection ws={w} />}
      {tab === 'documents' && <EdDocumentAssistant ws={w} />}
      {tab === 'disposition' && <DispositionSection ws={w} onResolveIdentity={has('er:register') ? () => setIdentityOpen(true) : undefined} />}

      {triageOpen && <TriagePanel encounter={enc} open onClose={() => setTriageOpen(false)} />}
      {identityOpen && <IdentityResolveDialog encounter={enc} open onClose={() => setIdentityOpen(false)} />}
      <TextPromptDialog
        open={unflagOpen}
        title="Remove MLC flag"
        description={mlcNumber
          ? `This encounter is no longer treated as a medico-legal case. Register entry ${mlcNumber} is kept (the number is not reused) and stays readable. The reason is saved in the audit trail.`
          : 'This encounter is no longer treated as a medico-legal case. The reason is saved in the audit trail.'}
        label="Why is this not an MLC?"
        placeholder="e.g. Selected by mistake — domestic fall, no foul play"
        multiline maxLength={500}
        confirmLabel="Remove MLC flag" danger
        pending={patch.isPending} error={unflagError}
        onClose={() => setUnflagOpen(false)}
        onConfirm={async (reason) => {
          setUnflagError(null);
          try {
            await patch.mutateAsync({ is_mlc: false, mlc_unflag_reason: reason.trim() });
            setUnflagOpen(false);
            if (tab === 'mlc' && !mlcNumber) setTab('overview');
            toast({ title: 'MLC flag removed', variant: 'success' });
          } catch (e) { setUnflagError(errorInfo(e).message); }
        }}
      />
      <NewLabOrderDialog
        open={labOpen}
        onClose={() => setLabOpen(false)}
        patient={{ id: enc.patient_id, name: enc.patient_name, uhid: enc.uhid, age: enc.age ?? enc.approx_age_years ?? null, gender: enc.gender ?? '' } as never}
        visitId={enc.visit_id}
        emergency={open}
        defaultPriority={w.settings.default_lab_priority}
        onCreated={() => { setLabOpen(false); ws.refetch(); }}
      />
    </div>
  );
}
